// app/javascript/controllers/turbo_frame_loader_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frame", "spinner"]

  connect() {
    this.show(false)

    // bind once
    this.boundStart   = this.onFetchStart.bind(this)
    this.boundStop    = this.onFetchStop.bind(this)
    this.boundStream  = this.onStreamEvent.bind(this)

    // attach to current frame
    if (this.hasFrameTarget) this.attachFrame(this.frameTarget)

    // listen for turbo-streams that mutate the frame
    document.addEventListener("turbo:before-stream-render", this.boundStream)
    document.addEventListener("turbo:after-stream-render",  this.boundStream)
  }

  disconnect() {
    this.detachFrame(this._currentFrame)
    document.removeEventListener("turbo:before-stream-render", this.boundStream)
    document.removeEventListener("turbo:after-stream-render",  this.boundStream)
  }

  frameTargetConnected(el) { this.attachFrame(el) }
  frameTargetDisconnected(el) { this.detachFrame(el) }

  attachFrame(el) {
    if (!el) return
    this.detachFrame(this._currentFrame)
    this._currentFrame = el

    // GET navigations into the frame
    el.addEventListener("turbo:before-fetch-request", this.boundStart)
    el.addEventListener("turbo:before-fetch-response", this.boundStop)
    el.addEventListener("turbo:frame-load", this.boundStop)
    el.addEventListener("turbo:fetch-request-error", this.boundStop)

    // form submits inside the frame
    el.addEventListener("turbo:submit-start", this.boundStart)
    el.addEventListener("turbo:submit-end",   this.boundStop)

    // safety when caching
    el.addEventListener("turbo:before-cache", this.boundStop)
  }

  detachFrame(el) {
    if (!el) return
    el.removeEventListener("turbo:before-fetch-request", this.boundStart)
    el.removeEventListener("turbo:before-fetch-response", this.boundStop)
    el.removeEventListener("turbo:frame-load", this.boundStop)
    el.removeEventListener("turbo:fetch-request-error", this.boundStop)
    el.removeEventListener("turbo:submit-start", this.boundStart)
    el.removeEventListener("turbo:submit-end", this.boundStop)
    el.removeEventListener("turbo:before-cache", this.boundStop)
    if (this._currentFrame === el) this._currentFrame = null
  }

  onFetchStart(event) {
    // only show for in-frame navigations
    if (!this._currentFrame) return
    // ignore non-GET fetches unless they are form submits
    const method = (event?.detail?.fetchOptions?.method || "GET").toUpperCase()
    if (method !== "GET" && event.type !== "turbo:submit-start") return
    this.show(true)
  }

  onFetchStop() { this.show(false) }

  onStreamEvent(event) {
    // hide spinner if a turbo-stream targets this frame
    const frame = this._currentFrame
    if (!frame) return
    const streamEl = event?.target || event?.detail?.newStream
    if (!streamEl) return
    if (this.streamTargetsFrame(streamEl, frame)) this.show(false)
  }

  streamTargetsFrame(streamEl, frame) {
    // <turbo-stream target="parts_panel">
    const t = streamEl.getAttribute?.("target")
    if (t && t === frame.id) return true

    // <turbo-stream targets="#parts_panel"> or any selector that includes the frame
    const sel = streamEl.getAttribute?.("targets")
    if (sel) {
      try {
        return Array.from(document.querySelectorAll(sel)).includes(frame)
      } catch (e) {
        console.warn("turbo-frame-loader: bad targets selector", sel, e)
      }
    }
    return false
  }

  show(on) {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !on)
    this.element?.setAttribute?.("data-loading", on ? "true" : "false")
  }
}
